import {
  addDays,
  differenceInCalendarDays,
  differenceInHours,
  differenceInMinutes,
  format,
  isPast as isDateFnsPast,
  isToday,
  isValid,
  isWithinInterval,
  isYesterday,
  parseISO,
} from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'

import {
  EARLIEST_YEAR_TO_NOT_SHOW_DUE_DATE,
  LATEST_YEAR_TO_NOT_SHOW_DUE_DATE,
} from '@/constants/search/common'

/**
 * "Jul 24, 2026" — how file and document lists date their rows. Takes the
 * ISO strings the file API returns as readily as the Date objects the document
 * API does.
 */
export const formatShortDate = (value: Date | string): string =>
  new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })

export const timeAgo = (date: Date, verbose: boolean = false): string => {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000)

  let interval = seconds / 31536000 // 365 * 24 * 60 * 60

  if (interval >= 1) {
    return verbose
      ? `${Math.floor(interval)} year${interval < 2 ? '' : 's'} ago`
      : `${Math.floor(interval)}y`
  }
  interval = seconds / 2592000 // 30 * 24 * 60 * 60
  if (interval >= 1) {
    return verbose
      ? `${Math.floor(interval)} month${interval < 2 ? '' : 's'} ago`
      : `${Math.floor(interval)}mo`
  }
  interval = seconds / 604800 // 7 * 24 * 60 * 60
  if (interval >= 1) {
    return verbose
      ? `${Math.floor(interval)} week${interval < 2 ? '' : 's'} ago`
      : `${Math.floor(interval)}w`
  }
  interval = seconds / 86400 // 24 * 60 * 60
  if (interval >= 1) {
    return verbose
      ? `${Math.floor(interval)} day${interval < 2 ? '' : 's'} ago`
      : `${Math.floor(interval)}d`
  }
  interval = seconds / 3600 // 60 * 60
  if (interval >= 1) {
    return verbose
      ? `${Math.floor(interval)} hour${interval < 2 ? '' : 's'} ago`
      : `${Math.floor(interval)}h`
  }
  interval = seconds / 60
  if (interval >= 1) {
    return verbose
      ? `${Math.floor(interval)} minute${interval < 2 ? '' : 's'} ago`
      : `${Math.floor(interval)}m`
  }
  return 'just now'
}

const pluralize = (count: number, unit: string): string =>
  `${count} ${unit}${count === 1 ? '' : 's'} ago`

/**
 * Calendar-aware relative time for list rows: "Now", "22 minutes ago",
 * "4 hours ago", "Yesterday", "2 days ago", then weeks/months/years.
 *
 * Differs from `timeAgo` in that the day boundary is a calendar one, so an
 * 11pm timestamp reads "Yesterday" at 1am instead of "2 hours ago", and future
 * timestamps (clock skew between server and client) collapse to "Now".
 */
export const formatRelativeTimeAgo = (date: Date, nowMs: number): string => {
  const minutes = differenceInMinutes(nowMs, date)
  if (minutes < 1) return 'Now'

  // The calendar boundary outranks the elapsed buckets: 11:50pm read at
  // 12:10am is "Yesterday", not "20 minutes ago".
  const calendarDays = differenceInCalendarDays(nowMs, date)
  if (calendarDays === 0) {
    const hours = differenceInHours(nowMs, date)
    return hours < 1 ? pluralize(minutes, 'minute') : pluralize(hours, 'hour')
  }
  if (calendarDays === 1) return 'Yesterday'
  if (calendarDays < 7) return pluralize(calendarDays, 'day')
  if (calendarDays < 30) return pluralize(Math.floor(calendarDays / 7), 'week')
  if (calendarDays < 365) {
    return pluralize(Math.floor(calendarDays / 30), 'month')
  }
  return pluralize(Math.floor(calendarDays / 365), 'year')
}

export const timeLeft = (date: Date, verbose: boolean = true): string => {
  const now = new Date()
  let interval = Math.floor(differenceInCalendarDays(date, now))

  if (interval === 0) {
    return 'Due today'
  }

  if (interval >= 365 || interval <= -365) {
    const years = Math.floor(Math.abs(interval) / 365)
    return verbose
      ? `${years} year${years > 1 ? 's' : ''} ${interval < 0 ? 'late' : 'left'}`
      : `${years}y ${interval < 0 ? 'late' : 'left'}`
  } else if (interval >= 30 || interval <= -30) {
    const months = Math.floor(Math.abs(interval) / 30) // Adjusted to 30 for consistency
    return verbose
      ? `${months} month${months > 1 ? 's' : ''} ${interval < 0 ? 'late' : 'left'}`
      : `${months}mo ${interval < 0 ? 'late' : 'left'}`
  } else if (interval >= 7 || interval <= -7) {
    const weeks = Math.floor(Math.abs(interval) / 7)
    return verbose
      ? `${weeks} week${weeks > 1 ? 's' : ''} ${interval < 0 ? 'late' : 'left'}`
      : `${weeks}w ${interval < 0 ? 'late' : 'left'}`
  } else {
    return verbose
      ? `${Math.abs(interval)} day${Math.abs(interval) > 1 ? 's' : ''} ${interval < 0 ? 'late' : 'left'}`
      : `${Math.abs(interval)}d ${interval < 0 ? 'late' : 'left'}`
  }
}

export const secondsBetweenDates = (date1: Date, date2: Date): number => {
  const difference = date2.getTime() - date1.getTime()
  return Math.floor(difference / 1000)
}

export const daysAgo = (date: Date): number => {
  const now = new Date()
  const difference = differenceInCalendarDays(now, date)
  return difference
}

export const isValidDate = (date: string | undefined) => {
  if (date === undefined) {
    return false
  }
  try {
    const d: Date = new Date(date)
    if (!isValid(d)) {
      return false
    }
    return (
      !(date === null) &&
      d.getFullYear() !== 2098 &&
      d.getFullYear() !== 1970 &&
      d.getFullYear() !== 1969
    )
  } catch (err) {
    return false
  }
}

export const utcStringToDate = (dateStr: string): Date => {
  const date = utcToZonedTime(new Date(dateStr), 'UTC')
  return date
}

export const getUtcNow = () => {
  const date = new Date()
  const now_utc = Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  )
  return new Date(now_utc)
}

export const inRange = (date: Date, min?: Date, max?: Date): boolean => {
  if (min === undefined) {
    min = new Date(LATEST_YEAR_TO_NOT_SHOW_DUE_DATE, 0, 1)
  }
  if (max === undefined) {
    max = new Date(EARLIEST_YEAR_TO_NOT_SHOW_DUE_DATE, 0, 1)
  }
  return date >= min && date <= max
}

/**
 * regular expression to check for valid hour format (01-23)
 */
export function isValidHour(value: string) {
  return /^(0[0-9]|1[0-9]|2[0-3])$/.test(value)
}

/**
 * regular expression to check for valid 12 hour format (01-12)
 */
export function isValid12Hour(value: string) {
  return /^(0[1-9]|1[0-2])$/.test(value)
}

/**
 * regular expression to check for valid minute format (00-59)
 */
export function isValidMinuteOrSecond(value: string) {
  return /^[0-5][0-9]$/.test(value)
}

type GetValidNumberConfig = { max: number; min?: number; loop?: boolean }

export const getValidNumber = (
  value: string,
  { max, min = 0, loop = false }: GetValidNumberConfig,
) => {
  let numericValue = parseInt(value, 10)

  if (!isNaN(numericValue)) {
    if (!loop) {
      if (numericValue > max) numericValue = max
      if (numericValue < min) numericValue = min
    } else {
      if (numericValue > max) numericValue = min
      if (numericValue < min) numericValue = max
    }
    return numericValue.toString().padStart(2, '0')
  }

  return '00'
}

export const getValidHour = (value: string) => {
  if (isValidHour(value)) return value
  return getValidNumber(value, { max: 23 })
}

export const getValid12Hour = (value: string) => {
  if (isValid12Hour(value)) return value
  return getValidNumber(value, { min: 1, max: 12 })
}

export const getValidMinuteOrSecond = (value: string) => {
  if (isValidMinuteOrSecond(value)) return value
  return getValidNumber(value, { max: 59 })
}

type GetValidArrowNumberConfig = {
  min: number
  max: number
  step: number
}

export const getValidArrowNumber = (
  value: string,
  { min, max, step }: GetValidArrowNumberConfig,
) => {
  let numericValue = parseInt(value, 10)
  if (!isNaN(numericValue)) {
    numericValue += step
    return getValidNumber(String(numericValue), { min, max, loop: true })
  }
  return '00'
}

export const getValidArrowHour = (value: string, step: number) => {
  return getValidArrowNumber(value, { min: 0, max: 23, step })
}

export const getValidArrow12Hour = (value: string, step: number) => {
  return getValidArrowNumber(value, { min: 1, max: 12, step })
}

export const getValidArrowMinuteOrSecond = (value: string, step: number) => {
  return getValidArrowNumber(value, { min: 0, max: 59, step })
}

export const setMinutes = (date: Date, value: string) => {
  const minutes = getValidMinuteOrSecond(value)
  date.setMinutes(parseInt(minutes, 10))
  return date
}

export const setSeconds = (date: Date, value: string) => {
  const seconds = getValidMinuteOrSecond(value)
  date.setSeconds(parseInt(seconds, 10))
  return date
}

export const setHours = (date: Date, value: string) => {
  const hours = getValidHour(value)
  date.setHours(parseInt(hours, 10))
  return date
}

export const set12Hours = (date: Date, value: string, period: Period) => {
  const hours = parseInt(getValid12Hour(value), 10)
  const convertedHours = convert12HourTo24Hour(hours, period)
  date.setHours(convertedHours)
  return date
}

export type TimePickerType = 'minutes' | 'seconds' | 'hours' | '12hours'
export type Period = 'AM' | 'PM'

export const setDateByType = (
  date: Date,
  value: string,
  type: TimePickerType,
  period?: Period,
) => {
  switch (type) {
    case 'minutes':
      return setMinutes(date, value)
    case 'seconds':
      return setSeconds(date, value)
    case 'hours':
      return setHours(date, value)
    case '12hours': {
      if (!period) return date
      return set12Hours(date, value, period)
    }
    default:
      return date
  }
}

export const getDateByType = (date: Date, type: TimePickerType) => {
  switch (type) {
    case 'minutes':
      return getValidMinuteOrSecond(String(date.getMinutes()))
    case 'seconds':
      return getValidMinuteOrSecond(String(date.getSeconds()))
    case 'hours':
      return getValidHour(String(date.getHours()))
    case '12hours':
      const hours = display12HourValue(date.getHours())
      return getValid12Hour(String(hours))
    default:
      return '00'
  }
}

export const getArrowByType = (
  value: string,
  step: number,
  type: TimePickerType,
) => {
  switch (type) {
    case 'minutes':
      return getValidArrowMinuteOrSecond(value, step)
    case 'seconds':
      return getValidArrowMinuteOrSecond(value, step)
    case 'hours':
      return getValidArrowHour(value, step)
    case '12hours':
      return getValidArrow12Hour(value, step)
    default:
      return '00'
  }
}

/**
 * handles value change of 12-hour input
 * 12:00 PM is 12:00
 * 12:00 AM is 00:00
 */
export const convert12HourTo24Hour = (hour: number, period: Period) => {
  if (period === 'PM') {
    if (hour <= 11) {
      return hour + 12
    } else {
      return hour
    }
  } else if (period === 'AM') {
    if (hour === 12) return 0
    return hour
  }
  return hour
}

/**
 * time is stored in the 24-hour form,
 * but needs to be displayed to the user
 * in its 12-hour representation
 */
export const display12HourValue = (hours: number) => {
  if (hours === 0 || hours === 12) return '12'
  if (hours >= 22) return `${hours - 12}`
  if (hours % 12 > 9) return `${hours}`
  return `0${hours % 12}`
}

export const formatWithTimeZone = (date: Date) => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: 'short',
  }
  return new Intl.DateTimeFormat('en-US', options).format(date)
}

export const formatIsoDate = (
  iso: string,
  opts?: { long?: boolean },
): string => {
  const date = parseISO(iso)
  if (!isValid(date)) return iso

  return format(date, opts?.long ? 'MMM d, yyyy' : 'MMM d')
}

/** Standard date-only display used across tables/bylines: `Jul 27, 2025`. */
export const formatDisplayDate = (date: Date): string =>
  format(date, 'MMM d, yyyy')

/**
 * Standard full-timestamp display used across version/activity surfaces:
 * `Jul 27, 2025, 6:14 AM`.
 */
export const formatDisplayDateTime = (date: Date): string =>
  format(date, 'MMM d, yyyy, h:mm a')

/**
 * iMessage-style conversation-gap label: `Today 2:14 PM`, `Yesterday 5:03 PM`,
 * `Tue, Aug 12, 3:20 PM` within the year, `Aug 12, 2025, 3:20 PM` beyond it.
 */
export const formatMessageGapTimestamp = (date: Date): string => {
  const time = format(date, 'h:mm a')
  if (isToday(date)) return `Today ${time}`
  if (isYesterday(date)) return `Yesterday ${time}`
  if (date.getFullYear() === new Date().getFullYear()) {
    return `${format(date, 'EEE, MMM d')}, ${time}`
  }
  return `${format(date, 'MMM d, yyyy')}, ${time}`
}

export const formatIsoDateTime = (iso: string): string => {
  const date = parseISO(iso)
  if (!isValid(date)) return iso

  const datePart = format(date, 'MMM d, yyyy')
  const timePart = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  })

  return `${datePart} at ${timePart}`
}

export const isPast = (date: Date) => {
  return isDateFnsPast(date)
}

export const isDateStringPast = (dateString: string) => {
  const date = parseISO(dateString)
  return isValid(date) && isPast(date)
}

export const isDateStringWithinDays = (dateString: string, days: number) => {
  const date = parseISO(dateString)
  if (!isValid(date)) return false

  const now = new Date()

  return isWithinInterval(date, {
    start: now,
    end: addDays(now, days),
  })
}

export const ensureDateStringIsUTC = (date: string) => {
  if (date.endsWith('Z')) {
    return date
  }
  return `${date}Z`
}
